// eslint-disable-next-line no-restricted-imports
import type {
  History,
  LinkProps,
  NavigateFn,
  ServerLocationProps,
} from '@reach/router';
// eslint-disable-next-line no-restricted-imports
import {
  Link,
  LocationProvider,
  ServerLocation,
  useNavigate,
} from '@reach/router';
import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

export type BlockerFunction = (args: {
  currentLocation: string;
  nextLocation: string | number;
}) => boolean;

export type AttemptNavigationFunction = (
  nextLocation: string | number
) => boolean;

interface BlockerContextType {
  blocked: boolean;
  register: (shouldBlock: BlockerFunction) => () => void;
  attemptNavigation: AttemptNavigationFunction;
}

const BlockerContext = createContext<BlockerContextType | undefined>(undefined);

export const BlockerProvider: FC<PropsWithChildren> = ({ children }) => {
  const blocker = useRef<false | BlockerFunction>(false);
  const isRevertingPop = useRef(false);

  const context = useMemo<BlockerContextType>(() => {
    const context: BlockerContextType = {
      blocked: blocker.current !== false,

      register(shouldBlock) {
        if (blocker.current) {
          console.error('We only support one blocker at once');
        }

        blocker.current = shouldBlock;

        globalThis.window?.addEventListener('beforeunload', handleUnload);
        globalThis.window?.addEventListener('popstate', handlePop);

        return () => {
          globalThis.window?.removeEventListener('beforeunload', handleUnload);
          globalThis.window?.removeEventListener('popstate', handlePop);
          blocker.current = false;
        };
      },

      attemptNavigation(nextLocation) {
        const shouldBlock = blocker.current;
        const currentLocation = globalThis.window.location.href;

        if (!shouldBlock) return true;
        if (!shouldBlock({ currentLocation, nextLocation })) {
          return true;
        }

        return false;
      },
    };

    const handleUnload = (event: BeforeUnloadEvent): void => {
      const target = globalThis.window.location.href;
      if (!context.attemptNavigation(target)) {
        event.preventDefault();
        event.returnValue = true;
      }
    };

    const handlePop = (): void => {
      if (isRevertingPop.current) {
        isRevertingPop.current = false;
        return;
      }
      if (!context.attemptNavigation(-1)) {
        isRevertingPop.current = true;
        window.history.go(+1);
      }
    };

    return context;
  }, [blocker]);

  return (
    <BlockerContext.Provider value={context}>
      {children}
    </BlockerContext.Provider>
  );
};

export const useBlockerContext = (): BlockerContextType => {
  const context = useContext(BlockerContext);

  if (!context) {
    throw new Error('useBlockerContext must be used within a BlockerProvider');
  }

  return context;
};

export const useBlocker = (shouldBlock: BlockerFunction): void => {
  const blocker = useBlockerContext();

  useEffect(() => {
    const unregister = blocker.register(shouldBlock);

    return () => {
      unregister();
    };
  }, [blocker, shouldBlock]);
};

export const useNavigateWrapper = (): NavigateFn => {
  const context = useContext(BlockerContext);
  const navigate = useNavigate();

  return useCallback<NavigateFn>(
    async (to, options) => {
      if (context?.attemptNavigation(to) === false) return;

      if (typeof to === 'number') {
        return navigate(to);
      } else {
        return navigate(to, options);
      }
    },
    [context, navigate]
  );
};

export const LinkWrapper: FC<
  React.PropsWithoutRef<LinkProps<unknown>> &
    React.RefAttributes<HTMLAnchorElement>
> = (props) => {
  const context = useContext(BlockerContext);
  if (!context) return <Link {...props} />;

  const { to, onClick, ...rest } = props;

  return (
    <Link
      {...rest}
      to={to}
      onClick={(event) => {
        if (!context.attemptNavigation(to)) {
          event.preventDefault();
          return;
        }

        onClick?.(event);
      }}
    />
  );
};

export const LocationProviderWrapper: FC<
  PropsWithChildren<{ history?: History | undefined }>
> = ({ children, ...props }) => {
  return (
    <LocationProvider {...props}>
      <BlockerProvider>{children}</BlockerProvider>
    </LocationProvider>
  );
};

export const ServerLocationWrapper: FC<
  PropsWithChildren<ServerLocationProps>
> = ({ children, ...props }) => {
  return (
    <ServerLocation {...props}>
      <BlockerProvider>{children}</BlockerProvider>
    </ServerLocation>
  );
};
